import React, { useMemo } from 'react';
import { PCAReportMetric } from 'c-sdk';
import { AutoGrid, NumberFormat, NumberFormatWhole } from 'c-components';
import type { TypographyProps } from '@mui/material';
import { Box, Typography } from '@mui/material';
import type { TranslationPath } from 'c-translation';
import { Translate } from 'c-translation';
import HoursMins from './HoursMins';

type Props = {
    metric: PCAReportMetric;
    actual: number;
    expected: number;
    performance: number;
    showVariance: boolean;
    inSchedule: number;
    outSchedule: number;
    unbooked: number;
};

const gridItemProps = { sx: { display: 'flex', justifyContent: 'center' } };

const MetricPerformance: React.FC<Props> = ({
    metric,
    actual,
    expected,
    performance,
    showVariance,
    inSchedule,
    outSchedule,
    unbooked,
}) => {
    const performanceColour = useMemo(() => {
        if (expected <= 0) return 'grey.600';

        return +performance >= 100 ? 'success.main' : 'warning.main';
    }, [expected, performance]);

    const variance = useMemo(() => actual - expected, [actual, expected]);
    const varianceColour = useMemo(() => {
        if (actual - expected > 0) {
            return 'success.main';
        }
        if (actual - expected === 0) {
            return null;
        }
        return 'warning.main';
    }, [actual, expected]);

    return (
        <>
            <AutoGrid spacing={0} xs={12} md={showVariance ? 3 : 4} GridItemProps={gridItemProps}>
                <Block
                    metric={metric}
                    count={expected > 0 ? performance : null}
                    countColor={performanceColour}
                    label={`Modules.Main.Campaigns.Overview.Reports.${metric}.overallTabTotalPerformance`}
                    percentage
                    asWholeNumber={false}
                />
                <Block
                    metric={metric}
                    count={expected}
                    countColor="grey.600"
                    label={`Modules.Main.Campaigns.Overview.Reports.${metric}.overallTabTotalTabExpectedTitle`}
                />
                <Block
                    metric={metric}
                    count={actual}
                    countColor="grey.600"
                    label={`Modules.Main.Campaigns.Overview.Reports.${metric}.overallTabTotalTabActualTitle`}
                />
                {showVariance && (
                    <Block
                        metric={metric}
                        count={variance}
                        countColor={varianceColour}
                        showPlusMinus
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        label="Variance +/-"
                    />
                )}
            </AutoGrid>
        </>
    );
};

type BlockProps = {
    metric: PCAReportMetric;
    asWholeNumber?: boolean;
    count?: number | string;
    countColor: TypographyProps['color'];
    label: TranslationPath;
    showPlusMinus?: boolean;
    percentage?: boolean;
};
const Block: React.FC<BlockProps> = ({
    metric,
    asWholeNumber = true,
    count,
    countColor,
    label,
    showPlusMinus = false,
    percentage = false,
}) => {
    const hasValue = count !== null && count !== undefined && count !== '';
    const numericCount = hasValue ? Number(count) : undefined;

    const sign =
        showPlusMinus && numericCount !== undefined
            ? numericCount > 0
                ? '+'
                : numericCount < 0
                ? '-'
                : ''
            : '';

    const absCount = hasValue ? Math.abs(numericCount as number) : undefined;

    return (
        <Box>
            <Typography variant="h2" color={countColor}>
                {metric === PCAReportMetric.Time && !percentage ? (
                    hasValue ? (
                        <Box
                            component="span"
                            sx={{ display: 'inline-flex', alignItems: 'baseline' }}
                        >
                            {showPlusMinus && sign && (
                                <Typography
                                    component="span"
                                    variant="h2"
                                    color={countColor}
                                    sx={{ mr: 0.25 }}
                                >
                                    {sign}
                                </Typography>
                            )}
                            <HoursMins seconds={absCount} variant="h2" />
                        </Box>
                    ) : (
                        '-'
                    )
                ) : (
                    <>
                        {!hasValue ? (
                            '-'
                        ) : (
                            <>
                                {showPlusMinus && <>{sign}</>}
                                {asWholeNumber ? (
                                    <NumberFormatWhole value={absCount} />
                                ) : (
                                    <NumberFormat value={absCount} />
                                )}
                                {percentage && '%'}
                            </>
                        )}
                    </>
                )}
            </Typography>
            <Typography variant="body1" color="grey.400" textAlign={{ xs: 'center', md: 'left' }}>
                <Translate path={label} />
            </Typography>
        </Box>
    );
};

export default MetricPerformance;
